// Comparison engine — pure functions, no I/O, no framework dependency.
// Used by workers/api/routes/compare.ts. Kept import-safe from the frontend
// too (tsconfig.worker.json includes src/utils) in case a future client-side
// preview is wanted, but the authoritative comparison always happens
// server-side against the D1 data.
//
// Ported deliberately from the working logic already proven in the source
// prototype (see Phase 1 report §4 "Architecture notes"): null means
// "not published," never 0, and a metric is simply excluded from scoring
// rather than penalised when either side is missing it.

import { GARMENT_TYPE_REGISTRY } from '@/data/garmentTypes';
import type {
  Category,
  ComparisonSettings,
  FitZone,
  FitZoneState,
  GarmentType,
  MeasurementKey,
  MeasurementRange,
  RegionDelta,
  SizeRow,
} from '@/types/domain';

/** Midpoint of a measurement range, or null if not published. */
export function midpoint(range: MeasurementRange): number | null {
  if (!range) return null;
  return (range[0] + range[1]) / 2;
}

/**
 * Builds a synthetic SizeRow from a FitMap Passport's raw body measurements
 * (all in cm), so the same findClosestSize/computeDelta pipeline that
 * compares two brand charts can also compare a user's own measurements
 * against every candidate brand — no separate "measurement comparison"
 * code path needed. Each measurement becomes a single-point range
 * ([v, v]), so its midpoint is exactly the entered value; a metric the
 * user never filled in stays null, exactly like an unpublished brand
 * measurement — it's excluded from scoring, never treated as 0.
 */
export function sizeRowFromMeasurements(measurements: Partial<Record<MeasurementKey, number>>): SizeRow {
  const point = (v: number | undefined): MeasurementRange => (v === undefined || v === null ? null : [v, v]);
  return {
    id: 'passport',
    sizeChartId: 'passport',
    label: 'Your measurements',
    alternateLabel: null,
    chest: point(measurements.chest),
    waist: point(measurements.waist),
    hip: point(measurements.hip),
    height: null,
    inseam: point(measurements.inseam),
    shoulder: point(measurements.shoulder),
    sleeve: point(measurements.sleeve),
    thigh: point(measurements.thigh),
    sortOrder: 0,
  };
}

/** Which measurements matter for a garment category, and their relative weight. */
export function metricsForCategory(
  category: Category,
  settings: ComparisonSettings
): Array<{ metric: 'chest' | 'waist' | 'hip'; weight: number }> {
  if (category === 'tops') {
    return [
      { metric: 'chest', weight: settings.topChestWeight },
      { metric: 'waist', weight: settings.topWaistWeight },
    ];
  }
  return [
    { metric: 'waist', weight: settings.bottomWaistWeight },
    { metric: 'hip', weight: settings.bottomHipWeight },
  ];
}

/**
 * Which measurements matter for a specific garment type (e.g. Jeans weighs
 * waist/hip/thigh/inseam), falling back to the plain category-level default
 * (metricsForCategory) when no garment type is given — preserving the exact
 * existing behaviour for the generic /equivalent flow, which never supplies
 * one. Config lives in src/data/garmentTypes.ts, never invented here.
 */
export function metricsForGarmentType(
  category: Category,
  garmentType: GarmentType | null | undefined,
  settings: ComparisonSettings
): Array<{ metric: MeasurementKey; weight: number }> {
  if (!garmentType) return metricsForCategory(category, settings);
  const config = GARMENT_TYPE_REGISTRY[garmentType];
  return (Object.entries(config.weights) as Array<[MeasurementKey, number]>).map(([metric, weight]) => ({
    metric,
    weight,
  }));
}

/**
 * Fit-distance score between a source row and a candidate target row, over
 * a given set of weighted metrics. Lower is closer. Missing measurements on
 * either side are excluded from the average rather than treated as 0
 * difference or as an automatic disqualification.
 *
 * Returns null if there are not enough valid (both-sides-published) metrics
 * to make a meaningful comparison — per spec: "Return no recommendation
 * where there are not enough valid measurements."
 */
export function fitDistanceScore(
  source: SizeRow,
  candidate: SizeRow,
  metrics: Array<{ metric: MeasurementKey; weight: number }>
): { score: number; matchedMetricCount: number } | null {
  let weightedSum = 0;
  let weightUsed = 0;
  let matchedMetricCount = 0;

  for (const { metric, weight } of metrics) {
    const sourceMid = midpoint(source[metric]);
    const candidateMid = midpoint(candidate[metric]);
    if (sourceMid === null || candidateMid === null) continue; // ignore, don't zero
    const diff = Math.abs(candidateMid - sourceMid);
    weightedSum += diff * weight;
    weightUsed += weight;
    matchedMetricCount += 1;
  }

  if (matchedMetricCount === 0 || weightUsed === 0) return null;

  // Renormalise by the weight actually used, so a row missing one metric
  // isn't unfairly penalised just for having less data.
  return { score: weightedSum / weightUsed, matchedMetricCount };
}

/**
 * Picks the closest target size row from a target chart's rows.
 * Returns null if no candidate has enough valid overlapping measurements.
 *
 * garmentType is optional and defaults to null, which reproduces the exact
 * pre-existing category-only behaviour — the generic /equivalent and
 * /brand-to-brand flows that don't pass one are unaffected by this change.
 */
export function findClosestSize(
  source: SizeRow,
  targetRows: SizeRow[],
  category: Category,
  settings: ComparisonSettings,
  garmentType: GarmentType | null = null
): { row: SizeRow; matchedMetricCount: number; score: number } | null {
  const metrics = metricsForGarmentType(category, garmentType, settings);
  let best: { row: SizeRow; matchedMetricCount: number; score: number } | null = null;

  for (const candidate of targetRows) {
    const result = fitDistanceScore(source, candidate, metrics);
    if (!result) continue;
    if (!best || result.score < best.score) {
      best = { row: candidate, matchedMetricCount: result.matchedMetricCount, score: result.score };
    }
  }
  return best;
}

/** Signed delta (target − source) per region, in cm. Null if either side is unpublished. */
export function computeDelta(source: SizeRow, target: SizeRow): RegionDelta {
  const diff = (a: MeasurementRange, b: MeasurementRange): number | null => {
    const am = midpoint(a);
    const bm = midpoint(b);
    if (am === null || bm === null) return null;
    return round1(bm - am);
  };
  return {
    chest: diff(source.chest, target.chest),
    waist: diff(source.waist, target.waist),
    hip: diff(source.hip, target.hip),
  };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Classifies a single delta value into a fit zone using configurable thresholds. */
export function classifyZone(
  delta: number | null,
  settings: ComparisonSettings
): FitZone {
  if (delta === null) return 'inactive';
  if (delta <= settings.tightThresholdCm) return 'tight'; // target smaller than source
  if (delta >= settings.looseThresholdCm) return 'loose'; // target bigger than source
  return 'neutral';
}

/** Which regions are relevant to shade for a category (used by both engine output and the avatar layer). */
export function relevantRegionsForCategory(category: Category): Array<'chest' | 'waist' | 'hip'> {
  return category === 'tops' ? ['chest', 'waist'] : ['waist', 'hip'];
}

export function computeZoneState(
  delta: RegionDelta,
  category: Category,
  settings: ComparisonSettings
): FitZoneState {
  const relevant = new Set(relevantRegionsForCategory(category));
  return {
    chest: relevant.has('chest') ? classifyZone(delta.chest, settings) : 'inactive',
    waist: relevant.has('waist') ? classifyZone(delta.waist, settings) : 'inactive',
    hip: relevant.has('hip') ? classifyZone(delta.hip, settings) : 'inactive',
  };
}

const REGION_LABEL: Record<'chest' | 'waist' | 'hip', string> = {
  chest: 'chest',
  waist: 'waist',
  hip: 'hip',
};

/**
 * Builds the human-readable, non-colour-dependent fit sentence required by
 * the spec, e.g.:
 * "Cotton On Small is approximately 4 cm looser through the chest and 1 cm
 *  tighter at the waist than adidas Small."
 *
 * Takes the same FitZoneState the avatar shading renders from (not just the
 * raw delta), and only ever says "tighter"/"looser" for a region when that
 * region's zone actually crossed the tight/loose threshold. A delta that's
 * nonzero but still inside the neutral band produces no clause — the same
 * way the avatar shows it as neutral (comfort fit), not tight or loose. This
 * keeps the sentence and the shading unable to disagree, since both are
 * read from the same classified zones rather than each re-deriving
 * direction from the raw number independently.
 */
export function buildFitDescription(params: {
  targetBrandName: string;
  targetSize: string;
  sourceBrandName: string;
  sourceSize: string;
  delta: RegionDelta;
  zones: FitZoneState;
  category: Category;
}): string {
  const { targetBrandName, targetSize, sourceBrandName, sourceSize, delta, zones, category } = params;
  const relevant = relevantRegionsForCategory(category);
  const clauses: string[] = [];

  for (const region of relevant) {
    const zone = zones[region];
    if (zone === 'inactive') {
      clauses.push(`${REGION_LABEL[region]} measurement not published for this comparison`);
      continue;
    }
    if (zone === 'neutral') continue; // comfort fit — no tighter/looser claim to make
    const d = delta[region] as number; // non-null: 'inactive' above covers the null case
    const magnitude = Math.abs(d);
    const direction = zone === 'loose' ? 'looser' : 'tighter';
    const at = region === 'waist' ? 'at the' : 'through the';
    clauses.push(`approximately ${magnitude} cm ${direction} ${at} ${REGION_LABEL[region]}`);
  }

  const prefix = `${targetBrandName} ${targetSize} is`;
  // sourceSize is empty when the source isn't a specific brand size at all
  // (e.g. a FitMap Passport measurement-based comparison) — "your
  // measurements" reads naturally alone, without a trailing empty size.
  const sourceLabel = sourceSize ? `${sourceBrandName} ${sourceSize}` : sourceBrandName;
  if (clauses.length === 0) {
    return `${prefix} a very close match to ${sourceLabel} across the measurements we have for this category.`;
  }
  return `${prefix} ${clauses.join(' and ')} than ${sourceLabel}.`;
}

/** Confidence indicator surfaced to the user alongside every result card. */
export function confidenceFromMatchCount(
  matchedMetricCount: number,
  totalRelevantMetrics: number
): 'high' | 'medium' | 'low' {
  if (matchedMetricCount >= totalRelevantMetrics) return 'high';
  if (matchedMetricCount >= 1) return 'medium';
  return 'low';
}
